/**
 * AnalyticsDashboard - Sprint 6 Enhanced
 * With Recharts visualizations and global filters
 * Sprint 18: Added Analytics Q&A Panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, Activity, Target, AlertTriangle, 
  CheckCircle2, Clock, Users, BarChart3, PieChart, RefreshCw,
  Filter, Calendar, X, FileText
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import QuickActionsWidget from '../components/QuickActionsWidget';
import AnalyticsQAPanel from '../components/AnalyticsQAPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const STATUS_COLORS = {
  'done': '#10b981',
  'in_progress': '#3b82f6',
  'not_started': '#9ca3af',
  'backlog': '#d1d5db',
  'blocked': '#ef4444'
};

// Filter Bar Component
const FilterBar = ({ filters, setFilters, onApply }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleClearFilters = () => {
    const cleared = { team: '', status: '', priority: '', startDate: '', endDate: '' };
    setLocalFilters(cleared);
    setFilters(cleared);
    onApply(cleared);
  };

  const handleApply = () => {
    setFilters(localFilters);
    onApply(localFilters);
  };

  const hasFilters = Object.values(localFilters).some(v => v);

  return (
    <Card className="mb-4" data-testid="filter-bar">
      <CardContent className="py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Filter className="h-4 w-4" />
            <span className="font-medium">Filters:</span>
          </div>
          
          {/* Status Filter */}
          <select
            value={localFilters.status || ''}
            onChange={(e) => setLocalFilters({...localFilters, status: e.target.value})}
            className="px-3 py-1.5 text-sm border rounded-md bg-white"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            <option value="done">Done</option>
            <option value="in_progress">In Progress</option>
            <option value="not_started">Not Started</option>
            <option value="backlog">Backlog</option>
            <option value="blocked">Blocked</option>
          </select>

          {/* Priority Filter */}
          <select
            value={localFilters.priority || ''}
            onChange={(e) => setLocalFilters({...localFilters, priority: e.target.value})}
            className="px-3 py-1.5 text-sm border rounded-md bg-white"
            data-testid="filter-priority"
          >
            <option value="">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={localFilters.startDate || ''}
              onChange={(e) => setLocalFilters({...localFilters, startDate: e.target.value})}
              className="px-2 py-1.5 text-sm border rounded-md"
              placeholder="Start"
              data-testid="filter-start-date"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={localFilters.endDate || ''}
              onChange={(e) => setLocalFilters({...localFilters, endDate: e.target.value})}
              className="px-2 py-1.5 text-sm border rounded-md"
              placeholder="End"
              data-testid="filter-end-date"
            />
          </div>

          <Button size="sm" onClick={handleApply} data-testid="apply-filters-btn">
            Apply
          </Button>

          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={handleClearFilters} data-testid="clear-filters-btn">
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
  <Card data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="pt-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon className={`h-5 w-5 text-${color}-500`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          <span>{Math.abs(trend)}% vs last period</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);
  const [capacityData, setCapacityData] = useState(null);
  const [risksData, setRisksData] = useState(null);
  const [demandsData, setDemandsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [filters, setFilters] = useState({
    team: '',
    status: '',
    priority: '',
    startDate: '',
    endDate: ''
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.team) params.append('team', filters.team);
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);
    return params.toString();
  }, [filters]);

  const fetchPortfolioData = useCallback(async () => {
    try {
      const qs = buildQueryString();
      const response = await fetch(`${API_URL}/api/reporting/portfolio${qs ? '?' + qs : ''}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPortfolioData(data);
      }
    } catch (err) {
      console.error('Failed to fetch portfolio data:', err);
    }
  }, [getAuthHeaders, buildQueryString]);

  const fetchDeliveryData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reporting/delivery?days=30`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveryData(data);
      }
    } catch (err) {
      console.error('Failed to fetch delivery data:', err);
    }
  }, [getAuthHeaders]);

  const fetchCapacityData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reporting/capacity`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCapacityData(data);
      }
    } catch (err) {
      console.error('Failed to fetch capacity data:', err);
    }
  }, [getAuthHeaders]);

  const fetchRisksData = useCallback(async () => {
    try {
      const qs = filters.priority ? `?priority=${filters.priority}` : '';
      const response = await fetch(`${API_URL}/api/reporting/risks${qs}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRisksData(data);
      }
    } catch (err) {
      console.error('Failed to fetch risks data:', err);
    }
  }, [getAuthHeaders, filters.priority]);

  const fetchDemandsData = useCallback(async () => {
    try {
      const qs = buildQueryString();
      const response = await fetch(`${API_URL}/api/reporting/demands/pipeline${qs ? '?' + qs : ''}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDemandsData(data);
      }
    } catch (err) {
      console.error('Failed to fetch demands data:', err);
    }
  }, [getAuthHeaders, buildQueryString]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchPortfolioData(),
        fetchDeliveryData(),
        fetchCapacityData(),
        fetchRisksData(),
        fetchDemandsData()
      ]);
      setLoading(false);
    };
    loadAllData();
  }, [fetchPortfolioData, fetchDeliveryData, fetchCapacityData, fetchRisksData, fetchDemandsData]);

  const refreshData = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchPortfolioData(),
      fetchDeliveryData(),
      fetchCapacityData(),
      fetchRisksData(),
      fetchDemandsData()
    ]).then(() => setLoading(false));
  }, [fetchPortfolioData, fetchDeliveryData, fetchCapacityData, fetchRisksData, fetchDemandsData]);

  const handleFilterApply = useCallback(() => {
    refreshData();
  }, [refreshData]);

  const getHealthColor = (score) => {
    if (score >= 80) return 'green';
    if (score >= 50) return 'yellow';
    return 'red';
  };

  const getHealthBadge = (score) => {
    if (score >= 80) return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-100 text-yellow-800">At Risk</Badge>;
    return <Badge className="bg-red-100 text-red-800">Critical</Badge>;
  };

  // Prepare chart data
  const statusChartData = portfolioData?.breakdown?.by_status 
    ? Object.entries(portfolioData.breakdown.by_status).map(([name, value]) => ({
        name: name.replace('_', ' '),
        value,
        fill: STATUS_COLORS[name] || '#9ca3af'
      }))
    : [];

  const sourceChartData = portfolioData?.charts?.portfolio_mix || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="analytics-dashboard">
      {/* Sprint 18: Analytics Q&A Panel */}
      <div className="mb-4">
        <AnalyticsQAPanel 
          onNavigate={(path) => navigate(path)}
          className="w-full max-w-2xl"
        />
      </div>

      {/* Header with Quick Actions */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
              <p className="text-gray-500">Unified view of ADO + Jira work items</p>
            </div>
            <Button variant="outline" onClick={refreshData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Global Filters */}
          <FilterBar filters={filters} setFilters={setFilters} onApply={handleFilterApply} />
        </div>
        
        {/* Quick Actions Widget - Desktop Only */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <QuickActionsWidget />
        </div>
      </div>
      
      {/* Quick Actions Widget - Mobile */}
      <div className="lg:hidden">
        <QuickActionsWidget />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="portfolio" className="flex items-center gap-2" data-testid="tab-portfolio">
            <Target className="h-4 w-4" />
            Portfolio
          </TabsTrigger>
          <TabsTrigger value="delivery" className="flex items-center gap-2" data-testid="tab-delivery">
            <Activity className="h-4 w-4" />
            Delivery
          </TabsTrigger>
          <TabsTrigger value="capacity" className="flex items-center gap-2" data-testid="tab-capacity">
            <Users className="h-4 w-4" />
            Capacity
          </TabsTrigger>
          <TabsTrigger value="risks" className="flex items-center gap-2" data-testid="tab-risks">
            <AlertTriangle className="h-4 w-4" />
            Risks
          </TabsTrigger>
          <TabsTrigger value="demands" className="flex items-center gap-2" data-testid="tab-demands">
            <FileText className="h-4 w-4" />
            Demands
          </TabsTrigger>
        </TabsList>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio" className="space-y-4">
          {portfolioData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Health Score"
                  value={`${portfolioData.summary.health_score}%`}
                  icon={Target}
                  color={getHealthColor(portfolioData.summary.health_score)}
                />
                <MetricCard
                  title="Active Projects"
                  value={portfolioData.summary.active_projects}
                  subtitle={`of ${portfolioData.summary.total_projects} total`}
                  icon={BarChart3}
                  color="blue"
                />
                <MetricCard
                  title="Completion Rate"
                  value={`${portfolioData.summary.completion_rate}%`}
                  icon={CheckCircle2}
                  color="green"
                />
                <MetricCard
                  title="Work Items"
                  value={portfolioData.summary.total_work_items}
                  icon={Activity}
                  color="purple"
                />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Work Distribution - Donut Chart */}
                <Card data-testid="portfolio-mix-chart">
                  <CardHeader>
                    <CardTitle className="text-lg">Portfolio Mix</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={sourceChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                            label={({name, value}) => `${name}: ${value}`}
                          >
                            {sourceChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Status Distribution - Bar Chart */}
                <Card data-testid="status-distribution-chart">
                  <CardHeader>
                    <CardTitle className="text-lg">Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statusChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={80} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {statusChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Story Points Summary */}
              <Card data-testid="story-points-card">
                <CardHeader>
                  <CardTitle className="text-lg">Story Points</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{portfolioData.story_points.total}</p>
                      <p className="text-sm text-gray-500">Total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{portfolioData.story_points.completed}</p>
                      <p className="text-sm text-gray-500">Completed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{portfolioData.story_points.remaining}</p>
                      <p className="text-sm text-gray-500">Remaining</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Delivery Tab */}
        <TabsContent value="delivery" className="space-y-4">
          {deliveryData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <MetricCard
                  title="Velocity"
                  value={`${deliveryData.metrics.velocity} pts/wk`}
                  icon={TrendingUp}
                  color="blue"
                />
                <MetricCard
                  title="Throughput"
                  value={`${deliveryData.metrics.throughput} items/wk`}
                  icon={Activity}
                  color="green"
                />
                <MetricCard
                  title="Cycle Time"
                  value={`${deliveryData.metrics.cycle_time_avg} days`}
                  icon={Clock}
                  color="orange"
                />
                <MetricCard
                  title="Predictability"
                  value={`${deliveryData.metrics.predictability}%`}
                  icon={Target}
                  color="purple"
                />
                <MetricCard
                  title="WIP"
                  value={deliveryData.metrics.wip_count}
                  subtitle="in progress"
                  icon={Activity}
                  color="yellow"
                />
              </div>

              {/* Cycle Time Chart */}
              <Card data-testid="cycle-time-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Cycle Time vs Throughput Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={deliveryData.charts?.cycle_time_trend || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="cycleTime" 
                          stroke="#f59e0b" 
                          name="Cycle Time (days)"
                          strokeWidth={2}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="throughput" 
                          stroke="#3b82f6" 
                          name="Throughput"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Completed by Type */}
              <Card data-testid="completed-by-type-card">
                <CardHeader>
                  <CardTitle className="text-lg">Completed by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={deliveryData.charts?.completed_by_type || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Capacity Tab */}
        <TabsContent value="capacity" className="space-y-4">
          {capacityData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Resources"
                  value={capacityData.summary.total_resources}
                  icon={Users}
                  color="blue"
                />
                <MetricCard
                  title="Overloaded"
                  value={capacityData.summary.overloaded_resources}
                  icon={AlertTriangle}
                  color={capacityData.summary.overloaded_resources > 0 ? 'red' : 'green'}
                />
                <MetricCard
                  title="Avg Utilization"
                  value={`${capacityData.summary.avg_utilization}%`}
                  icon={Activity}
                  color="purple"
                />
                <MetricCard
                  title="Unassigned Items"
                  value={capacityData.summary.unassigned_items}
                  icon={BarChart3}
                  color="orange"
                />
              </div>

              {/* Resource Utilization Chart */}
              <Card data-testid="utilization-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Resource Utilization</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={capacityData.resources || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis domain={[0, 150]} />
                        <Tooltip />
                        <Bar dataKey="utilization" name="Utilization %" radius={[4, 4, 0, 0]}>
                          {(capacityData.resources || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.utilization > 100 ? '#ef4444' : entry.utilization > 80 ? '#f59e0b' : '#10b981'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="space-y-4">
          {risksData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <MetricCard
                  title="High Risk Projects"
                  value={risksData.summary.high_risk_projects}
                  icon={AlertTriangle}
                  color="red"
                />
                <MetricCard
                  title="Medium Risk"
                  value={risksData.summary.medium_risk_projects}
                  icon={AlertTriangle}
                  color="yellow"
                />
                <MetricCard
                  title="Open Issues"
                  value={risksData.summary.open_issues}
                  icon={Activity}
                  color="orange"
                />
                <MetricCard
                  title="Critical Issues"
                  value={risksData.summary.critical_issues}
                  icon={AlertTriangle}
                  color="red"
                />
              </div>

              {/* Risk Heat Matrix */}
              <Card data-testid="risk-heatmap">
                <CardHeader>
                  <CardTitle className="text-lg">Issue Aging Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={Object.entries(risksData.issues?.aging || {}).map(([bucket, count]) => ({
                          bucket,
                          count
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" name="Issues" radius={[4, 4, 0, 0]}>
                          {Object.keys(risksData.issues?.aging || {}).map((_, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={index === 3 ? '#ef4444' : index === 2 ? '#f59e0b' : index === 1 ? '#fbbf24' : '#10b981'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Project Risk Table */}
              <Card data-testid="risk-projects-table">
                <CardHeader>
                  <CardTitle className="text-lg">At-Risk Projects</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Project</th>
                          <th className="text-left py-2 px-3">Risk Score</th>
                          <th className="text-left py-2 px-3">Level</th>
                          <th className="text-left py-2 px-3">Factors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(risksData.projects || []).slice(0, 5).map((project, idx) => (
                          <tr key={project.id || idx} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{project.name}</td>
                            <td className="py-2 px-3">{project.risk_score}</td>
                            <td className="py-2 px-3">
                              <Badge className={
                                project.risk_level === 'high' ? 'bg-red-100 text-red-800' :
                                project.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }>
                                {project.risk_level}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-xs">
                              {(project.risk_factors || []).join(', ').substring(0, 50)}...
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Demands Tab (Sprint 7) */}
        <TabsContent value="demands" className="space-y-4">
          {demandsData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Demands"
                  value={demandsData.summary?.total_demands || 0}
                  icon={FileText}
                  color="blue"
                />
                <MetricCard
                  title="In Pipeline"
                  value={demandsData.summary?.active_in_pipeline || 0}
                  subtitle="intake → governance"
                  icon={Activity}
                  color="yellow"
                />
                <MetricCard
                  title="Approved"
                  value={demandsData.summary?.approved || 0}
                  icon={CheckCircle2}
                  color="green"
                />
                <MetricCard
                  title="Approval Rate"
                  value={`${demandsData.summary?.approval_rate || 0}%`}
                  icon={Target}
                  color="purple"
                />
              </div>

              {/* Pipeline Funnel Chart */}
              <Card data-testid="demands-funnel-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Demand Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={demandsData.charts?.funnel || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="stage" type="category" width={100} />
                        <Tooltip />
                        <Bar dataKey="count" name="Demands" radius={[0, 4, 4, 0]}>
                          {(demandsData.charts?.funnel || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={
                                entry.stage === 'Approved' ? '#10b981' :
                                entry.stage === 'Rejected' ? '#ef4444' :
                                COLORS[index % COLORS.length]
                              } 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Status Counts */}
              <Card data-testid="demands-status-counts">
                <CardHeader>
                  <CardTitle className="text-lg">Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-4 text-center">
                    {['intake', 'assessment', 'governance', 'approved', 'rejected'].map(status => (
                      <div key={status} className="p-3 rounded-lg bg-gray-50">
                        <p className={`text-2xl font-bold ${
                          status === 'approved' ? 'text-green-600' :
                          status === 'rejected' ? 'text-red-600' :
                          'text-blue-600'
                        }`}>
                          {demandsData.counts?.[status] || 0}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">{status}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
